import { Text } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import { explainSearch } from 'clientAPI/explain';
import ProductRankingClient from 'clientAPI/productRanking';
import SearchOptionsVersionClient from 'clientAPI/searchOptionsVersion';
import Loader from 'components/Loader';
import React, { Suspense } from 'react';
import { useRevalidator } from 'react-router-dom';
import { QUERY_KEYS } from '../../constants';

// eslint-disable-next-line import/no-named-as-default
import ExplainRecallDetails, {
  JsonViewerCustom,
  Title,
} from './ExplainRecallDetails';

type SearchProps = {
  searchTerms: string;
  language?: string; //'zh-Hans' | 'en';
  marketplace?: string;
  consumerChannelId?: string;
  searchOptions: { [key: string]: string };
  sort?: string;
  expandedSearchOptions?: { [key: string]: string };
};

function useExplainSearch(props: SearchProps) {
  const revalidator = useRevalidator();
  const searchFlow = props.searchOptions.searchFlow;
  const multiPathRecallEnable = props.searchOptions.multiPathRecall;
  const propsCopy = JSON.parse(JSON.stringify(props));
  delete propsCopy.searchOptions['upmid'];
  const body = JSON.stringify(propsCopy);

  // Use the appropriate query key based on the search flow and multiPathRecallEnable
  let queryKey;
  if (searchFlow === 'old') {
    queryKey = QUERY_KEYS.EXPLAIN_SEARCH_LEGACY;
  } else if (searchFlow === 'new' && multiPathRecallEnable === 'enable') {
    queryKey = QUERY_KEYS.EXPLAIN_SEARCH_NEW_MULTIPATH;
  } else {
    queryKey = QUERY_KEYS.EXPLAIN_SEARCH_NEW;
  }

  return useQuery([queryKey, body], explainSearch(), {
    staleTime: Infinity,
    suspense: true,
    onError: (err: keyable) => err.status === 401 && revalidator.revalidate(),
  });
}

function useProductRanking(props: SearchProps) {
  const revalidator = useRevalidator();
  const expandedSearchOptions =
    props.expandedSearchOptions || props.searchOptions;
  const isLTR = expandedSearchOptions.rankingServiceStrategy === 'ltr';

  // Build searchOptions string from expandedSearchOptions
  const searchOptionsString = Object.entries(expandedSearchOptions)
    .filter(([key, value]) => value && key !== 'version')
    .map(([key, value]) => `${key}:${value}`)
    .join(',');

  return useQuery(
    [QUERY_KEYS.PRODUCT_RANKING, props.searchTerms, searchOptionsString],
    async () => {
      return ProductRankingClient.ranking({
        marketplace: props.marketplace || 'CN',
        language: props.language || 'zh-Hans',
        channelId: props.consumerChannelId || '',
        searchTerms: props.searchTerms,
        searchOptions: searchOptionsString,
        navigationType: 'SEARCH',
        explain: true,
        productIdList: [],
      });
    },
    {
      staleTime: Infinity,
      suspense: false,
      enabled: !!props.searchTerms && isLTR,
      retry: false,
      onError: (err: keyable) => {
        console.error('Product ranking error:', err);
        err.status === 401 && revalidator.revalidate();
      },
    },
  );
}

// eslint-disable-next-line import/no-unused-modules
export function Explain(props: SearchProps) {
  return (
    <>
      <Text font='display-5' as='h3' className='tw-my-2'>
        Explain
      </Text>
      {!props.searchTerms ? (
        <Text>Missing searchTerms</Text>
      ) : (
        <ExplainShell>
          <ExplainCore {...props} />
        </ExplainShell>
      )}
    </>
  );
}

function ExplainShell({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

function ExplainCore(props: SearchProps) {
  const { data } = useExplainSearch(props);
  const versionValue = props.searchOptions.version;

  // Fetch versions data to expand version if needed
  const { data: versionsData } = useQuery(
    [QUERY_KEYS.SEARCH_OPTIONS_VERSION],
    () => SearchOptionsVersionClient.getAll(),
    {
      staleTime: Infinity,
      enabled: !!versionValue,
    },
  );

  // Compute expandedSearchOptions: use expanded version if found, otherwise use searchOptions
  const expandedSearchOptions = React.useMemo(() => {
    if (versionValue && versionsData) {
      const matchedVersion = versionsData.find(
        (v) => v.searchVersionNumber === versionValue,
      );
      if (matchedVersion?.searchOptions) {
        return matchedVersion.searchOptions as { [key: string]: string };
      }
    }
    return props.searchOptions;
  }, [versionValue, versionsData, props.searchOptions]);

  const isLTR = expandedSearchOptions.rankingServiceStrategy === 'ltr';
  const { data: rankingData, isLoading: isRankingLoading } = useProductRanking({
    ...props,
    expandedSearchOptions,
  });

  // Extract rule info from explain.ruleInfo
  const ruleInfo = rankingData?.explain?.ruleInfo;
  const ruleName = ruleInfo?.name;
  const ruleId = ruleInfo?._id;

  return (
    <div>
      {isLTR && (
        <section className='tw-py-2'>
          <Title>Ranking Rule Info</Title>
          {isRankingLoading ? (
            <Text>Loading ranking info...</Text>
          ) : ruleName ? (
            <div className='tw-flex tw-flex-wrap tw-gap-2 tw-items-center'>
              Name:{' '}
              <a
                href={`/ranking/edit-rule/${ruleId}`}
                target='_blank'
                rel='noopener noreferrer'
                className='tw-px-3 tw-py-1 tw-bg-blue-100 tw-text-blue-800 tw-rounded-full tw-text-sm tw-no-underline hover:tw-underline'
              >
                {ruleName}
              </a>
            </div>
          ) : (
            <Text>No ranking rule applied</Text>
          )}
        </section>
      )}
      {data.queryProcessorMultiStageResponse?.map(
        (data: any, index: number) => (
          // eslint-disable-next-line react/jsx-no-comment-textnodes, react/jsx-key
          <section className='tw-py-2'>
            <Title>
              Multi Path Recall Details For Stage: {data.result.stageName}
            </Title>
            <ExplainRecallDetails
              explainRecallData={data}
              explainRecallIndex={index}
              defaultShowRecallDetailsFlag={false}
            />
          </section>
        ),
      )}
      {data.result ? (
        <section className='tw-py-2'>
          <Title>Single Path Recall Details</Title>
          <ExplainRecallDetails
            explainRecallData={data}
            explainRecallIndex={0}
            defaultShowRecallDetailsFlag={true}
          />
        </section>
      ) : null}
      {data.strategy && data.strategy.name ? (
        <section className='tw-py-2'>
          <Title>Recall Strategy Details</Title>
          <JsonViewerCustom data={data.strategy} />
        </section>
      ) : null}
    </div>
  );
}
